import { z } from "zod";
import { atom } from "jotai";
import { JotaiTool } from "../../index";

const parameters = z.object({
  todos: z
    .string()
    .describe(
      "Must be proper JSON. Array of todo objects with format: [{ 'content': string, 'status': 'pending' | 'in_progress' | 'completed' | 'failed', 'id': string }]",
    ),
});

const description = `Manage a structured task list for your current coding session via a text node on the canvas. This helps you track progress, organize complex tasks, and demonstrate thoroughness.

🚨 **SEQUENCE VALIDATION FOR APP INTEGRATIONS:**
When app integrations are detected, automatically create checkpoint todos in this order:
1. "Authenticate [app name] - CHECKPOINT 1/3"
2. "Step Test with sample data - CHECKPOINT 2/3" 
3. "Configure field mappings - CHECKPOINT 3/3"

⛔ **SEQUENCE VALIDATION RULES:**
- Cannot mark "Test" as in_progress if "Auth" not completed
- Cannot mark "Field Mapping" as in_progress if "Test" not completed  
- If sequence violation attempted, show error: "SEQUENCE ERROR - Must complete CHECKPOINT [X/3] first"

## When to Use This Tool
Use this tool proactively in these scenarios:

1. **Complex multi-step tasks** - When a task requires 3 or more distinct steps or actions
2. **Non-trivial and complex tasks** - Tasks that require careful planning or multiple operations
3. **App integrations** - AUTOMATICALLY create checkpoint sequence todos (1/3, 2/3, 3/3)
4. **User explicitly requests todo list** - When the user directly asks you to use the todo list
5. **User provides multiple tasks** - When users provide a list of things to be done (numbered or comma-separated)
6. **After receiving new instructions** - Immediately capture user requirements as todos
7. **When you start working on a task** - Mark it as in_progress BEFORE beginning work. Ideally you should only have one todo as in_progress at a time
8. **After completing a task** - Mark it as completed and add any new follow-up tasks discovered during implementation

## When NOT to Use This Tool

Skip using this tool when:
1. There is only a single, straightforward task
2. The task is trivial and tracking it provides no organizational benefit
3. The task can be completed in less than 3 trivial steps
4. The task is purely conversational or informational

## Task States and Management

1. **Task States**: Use these states to track progress:
   - pending: Task not yet started (⧠)
   - in_progress: Currently working on (⧗) (limit to ONE task at a time)
   - completed: Task finished successfully (✓)

2. **Task Management**:
   - Update task status in real-time as you work
   - Mark tasks complete IMMEDIATELY after finishing (don't batch completions)
   - Only have ONE task in_progress at any time
   - Complete current tasks before starting new ones
   - Remove tasks that are no longer relevant from the list entirely

3. **SEQUENCE VALIDATION FOR APP INTEGRATIONS**:
   - **CHECKPOINT 1/3 (Auth)**: Must be completed ✓ before CHECKPOINT 2/3 can start ⧗
   - **CHECKPOINT 2/3 (Test)**: Must be completed ✓ before CHECKPOINT 3/3 can start ⧗
   - **SEQUENCE BLOCKING**: If trying to mark later checkpoint as in_progress while earlier is incomplete, show: "SEQUENCE ERROR - Cannot start CHECKPOINT [Y/3] until CHECKPOINT [X/3] is completed ✓"
   - **VISUAL PROGRESS**: Show checkpoint status with symbols: ⧠ (blocked) ⧗ (in progress) ✓ (complete)

4. **Task Completion Requirements**:
   - ONLY mark a task as completed when you have FULLY accomplished it
   - If you encounter errors, blockers, or cannot finish, keep the task as in_progress
   - When blocked, create a new task describing what needs to be resolved
   - Never mark a task as completed if:
     - Tests are failing
     - Implementation is partial
     - You encountered unresolved errors
     - You couldn't find necessary files or dependencies

4. **Task Breakdown**:
   - Create specific, actionable items
   - Break complex tasks into smaller, manageable steps
   - Use clear, descriptive task names

## USER CHECK-IN PROTOCOL

**MANDATORY**: After completing each todo task, pause for user confirmation:

1. **ON TASK COMPLETION** - When marking a task as 'completed':
   - Display what was accomplished
   - Show remaining tasks  
   - Ask user if ready to continue

2. **CHECK-IN MESSAGE FORMAT**:
   "✅ Completed: [task name]
   
   What I did: [brief summary]
   Next up: [next task name]
   
   Ready to proceed? Reply 'continue' when ready."

3. **WAIT FOR CONFIRMATION** - Do not start next task until user responds with confirmation like "continue", "yes", "proceed", etc.

4. **STRICT RULE**: Check-in is REQUIRED for ALL completed tasks except these specific internal tasks ONLY:
   - "Create todo list" 
   - "Update todo list"
   - "Mark task as in_progress"
   - "Mark task as completed"
   
   **ALL OTHER TASKS** require user check-in - including API calls, component creation, data operations, testing, etc.

The tool manages your task list internally for organization and planning purposes without creating visible UI elements.`;

export const manageTodosToolAtom = atom<JotaiTool<z.infer<typeof parameters>>>(
  () => ({
    name: "manage_todos",
    description: description,
    parameters: parameters,
    execute: atom(
      null,
      async (get, set, { args }: { args: z.infer<typeof parameters> }) => {
        try {
          // Parse todos from the provided JSON string
          let todos: Array<{
            content: string;
            status: "pending" | "in_progress" | "completed" | "failed";
            id: string;
          }>;

          try {
            todos = JSON.parse(args.todos);
          } catch {
            return {
              $error: {
                message: `Invalid todos format. Expected JSON array of todo objects. Received: ${args.todos}`,
              },
            };
          }

          // Note: Todo list is managed internally without creating visible UI nodes
          // This allows AI to track tasks without cluttering the user's canvas

          const pendingCount = todos.filter(
            (t) => t.status === "pending",
          ).length;
          const inProgressCount = todos.filter(
            (t) => t.status === "in_progress",
          ).length;
          const completedCount = todos.filter(
            (t) => t.status === "completed",
          ).length;
          return {
            content: [
              {
                type: "text",
                text: `Todo list updated: ${completedCount} completed, ${inProgressCount} in progress, ${pendingCount} pending tasks`,
              },
            ],
          };
        } catch (error) {
          return {
            $error: {
              message: `Failed to update todo list: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          };
        }
      },
    ),
  }),
);
